import { useState, type FC } from 'react'
import {
    Modal,
    Stack,
    Group,
    Text,
    Badge,
    Button,
    Checkbox,
    Paper,
} from '@mantine/core'
import { IconPlus, IconSend } from '@tabler/icons-react'
import type { AudienceConfiguration } from '@/lib/domain'
import {
    destinationConnectionReady,
    destinationType,
    DESTINATION_TYPE_LABEL,
} from '@/lib/marketplaceDestinations'
import { segmentKey } from '../api'
import { useSegmentStubs } from '../queries'
import type { Destination } from '../types'
import { AddConnectionModal } from './AddConnectionModal'
import { DestinationConnectionModal } from './DestinationConnectionModal'

export interface DistributeModalProps {
    // One or more segments to send at once. A single-segment distribute passes
    // [config]; an audience-level distribute passes all its segments.
    segments: AudienceConfiguration[]
    threadId: string
    title: string
    opened: boolean
    onClose: () => void
}

// Shared distribute sheet: pick destinations and send every given segment to the
// checked set. Destinations are pre-checked from history (where any segment
// already goes); on first distribution we fall back to the ones whose connection
// is ready. Anything that needs setup lands as a per-delivery "next step" instead
// of blocking the rest.
export const DistributeModal: FC<DistributeModalProps> = ({
    segments,
    threadId,
    title,
    opened,
    onClose,
}) => {
    const { destinations, lifecycleFor, setSegmentDistributions, savedConfig } =
        useSegmentStubs()

    const [checked, setChecked] = useState<string[]>(() => {
        const current = new Set<string>()
        for (const config of segments) {
            for (const delivery of lifecycleFor(segmentKey(config), threadId)
                .deliveries) {
                current.add(delivery.distribution.destinationId)
            }
        }
        if (current.size > 0) return [...current]
        // Default to the destinations whose connection is ready to deliver.
        return destinations
            .filter((d) => destinationConnectionReady(d.platform, savedConfig))
            .map((d) => d.id)
    })

    // Create-a-destination chain: add from the catalog, then set up its
    // connection — without leaving the distribute sheet. A newly-added
    // destination is auto-checked so it's included in this send.
    const [addOpen, setAddOpen] = useState(false)
    const [connecting, setConnecting] = useState<Destination | null>(null)

    const toggle = (id: string) =>
        setChecked((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        )

    const handleDistribute = () => {
        const chosen = destinations.filter((d) => checked.includes(d.id))
        for (const config of segments) {
            setSegmentDistributions({
                threadId,
                segmentKey: segmentKey(config),
                destinations: chosen,
            })
        }
        onClose()
    }

    const subject =
        segments.length === 1
            ? 'this segment'
            : `all ${segments.length} segments`
    const destLabel = checked.length === 1 ? 'destination' : 'destinations'

    return (
        <>
            <Modal
                opened={opened}
                onClose={onClose}
                title={
                    <Text fw={600}>
                        <IconSend
                            size={18}
                            style={{ verticalAlign: 'text-bottom' }}
                        />{' '}
                        {title}
                    </Text>
                }
                centered
                size='lg'
            >
                <Stack gap='md'>
                    <Text size='sm' c='dimmed'>
                        Sending {subject} to the destinations you usually use.
                        Anything that needs setup won&apos;t block the rest —
                        it&apos;ll show up as a next step.
                    </Text>

                    <Stack gap='xs'>
                        {destinations.map((dest: Destination) => (
                            <Paper key={dest.id} withBorder p='sm' radius='md'>
                                <Group
                                    justify='space-between'
                                    align='flex-start'
                                    wrap='nowrap'
                                >
                                    <Checkbox
                                        checked={checked.includes(dest.id)}
                                        onChange={() => toggle(dest.id)}
                                        label={
                                            <Stack gap={2}>
                                                <Group gap='xs'>
                                                    <Text size='sm' fw={500}>
                                                        {dest.platform}
                                                    </Text>
                                                    <Badge
                                                        size='xs'
                                                        variant='light'
                                                        color='gray'
                                                    >
                                                        {
                                                            DESTINATION_TYPE_LABEL[
                                                                destinationType(
                                                                    dest.platform
                                                                )
                                                            ]
                                                        }
                                                    </Badge>
                                                </Group>
                                            </Stack>
                                        }
                                    />
                                    {!destinationConnectionReady(
                                        dest.platform,
                                        savedConfig
                                    ) && (
                                        <Badge variant='light' color='warning'>
                                            Needs setup
                                        </Badge>
                                    )}
                                </Group>
                            </Paper>
                        ))}
                        <Button
                            variant='subtle'
                            size='compact-sm'
                            leftSection={<IconPlus size={16} />}
                            onClick={() => setAddOpen(true)}
                            style={{ alignSelf: 'flex-start' }}
                        >
                            Add a destination
                        </Button>
                    </Stack>

                    <Group justify='flex-end' mt='xs'>
                        <Button variant='default' onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            leftSection={<IconSend size={16} />}
                            onClick={handleDistribute}
                        >
                            Distribute to {checked.length} {destLabel}
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <AddConnectionModal
                existingPlatforms={destinations.map((d) => d.platform)}
                opened={addOpen}
                onClose={() => setAddOpen(false)}
                onAdded={(dest) => {
                    // Include it in this send, then chain to connection setup.
                    setChecked((prev) =>
                        prev.includes(dest.id) ? prev : [...prev, dest.id]
                    )
                    setConnecting(dest)
                }}
            />

            {connecting && (
                <DestinationConnectionModal
                    destination={connecting}
                    opened
                    onClose={() => setConnecting(null)}
                />
            )}
        </>
    )
}
